import { addLocale, getLocale } from '@umijs/max';
import zhCN from 'antd/locale/zh_CN';
import enUS from 'antd/locale/en_US';
import jaJP from 'antd/locale/ja_JP';
import zhCNMessages from '../locales/zh-CN';
import enUSMessages from '../locales/en-US';
import jaJPMessages from '../locales/ja-JP';

const localeMap: Record<string, any> = {
  'zh-CN': zhCN,
  'en-US': enUS,
  'ja-JP': jaJP,
};

const baseMessagesMap: Record<string, Record<string, string>> = {
  'zh-CN': zhCNMessages as Record<string, string>,
  'en-US': enUSMessages as Record<string, string>,
  'ja-JP': jaJPMessages as Record<string, string>,
};

const getMenuLocaleStorageKey = (locale: string, userId?: number | string) =>
  `MENU_LOCALE_OVERRIDES_${userId ?? 'guest'}_${locale}`;

/**
 * 动态更新语言包
 * @param key 国际化 ID
 * @param value 翻译文本
 * @param locale 语言（可选，默认当前语言）
 * @param userId 用户 ID（可选，用于隔离不同用户的覆盖）
 */
export const updateLocale = (key: string, value: string, locale?: string, userId?: number | string) => {
  const currentLocale = locale || getLocale();
  const storageKey = getMenuLocaleStorageKey(currentLocale, userId);

  // 若值与基础翻译一致，清除多余的覆盖缓存，无需 addLocale
  const baseMessages = baseMessagesMap[currentLocale] ?? {};
  if (baseMessages[key] === value) {
    try {
      const cachedOverrides = localStorage.getItem(storageKey);
      if (cachedOverrides) {
        const overrides = JSON.parse(cachedOverrides);
        if (key in overrides) {
          delete overrides[key];
          localStorage.setItem(storageKey, JSON.stringify(overrides));
        }
      }
    } catch (e) {
      console.error('Failed to clean MENU_LOCALE_OVERRIDES', e);
    }
    return;
  }

  // 值与基础翻译不同，保存覆盖
  try {
    const cachedOverrides = localStorage.getItem(storageKey) || '{}';
    const overrides = JSON.parse(cachedOverrides);
    overrides[key] = value;
    localStorage.setItem(storageKey, JSON.stringify(overrides));
  } catch (e) {
    console.error('Failed to save MENU_LOCALE_OVERRIDES', e);
  }

  addLocale(
    currentLocale,
    { [key]: value },
    {
      momentLocale: currentLocale,
      antd: localeMap[currentLocale] || zhCN,
    },
  );
};

/** 清除旧格式的 MENU_LOCALE_OVERRIDES 缓存（无 userId 前缀） */
export const cleanLegacyMenuLocaleCache = () => {
  const locales = ['zh-CN', 'en-US', 'ja-JP'];
  for (const locale of locales) {
    localStorage.removeItem(`MENU_LOCALE_OVERRIDES_${locale}`);
  }
};

/**
 * 初始化菜单国际化
 * 从 localStorage 加载覆盖的菜单名称并应用
 * @param userId 用户 ID（可选，用于隔离不同用户的覆盖）
 */
export const initMenuLocales = (userId?: number | string) => {
  const currentLocale = localStorage.getItem('umi_locale') || 'zh-CN';
  const storageKey = getMenuLocaleStorageKey(currentLocale, userId);

  try {
    const cachedOverrides = localStorage.getItem(storageKey);
    if (cachedOverrides) {
      const overrides = JSON.parse(cachedOverrides);
      if (Object.keys(overrides).length > 0) {
        addLocale(
          currentLocale,
          overrides,
          {
            momentLocale: currentLocale,
            antd: localeMap[currentLocale] || zhCN,
          },
        );
      }
    }
  } catch (e) {
    console.error('Failed to init menu locales', e);
  }
};


